import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Field } from '../new-field';
import { InputFieldParams } from './input-field';
import Slider from 'rc-slider';
import styled from 'styled-components';
import { debounce } from 'utils';
import { useSiteConfigContext } from 'style-context';
import { getColor, ProductModuleDefinitionEmbeddedConfig } from 'site-config';
import { getFieldError, getNextComponentId, prefillBehavior, setFormFocusOrBlur } from './utils';
import { getSteppedFormInputStyle, getSteppedFormLabelColor, SteppedFormButton } from './utils/stepped-form';
import { Colors } from 'rootstrap/global-styles/colors';
import { StyledSliderRootSchemaComponent } from './styles/root-schema-component-style';
import { getValidationMessage, ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { formatToNumber } from './utils/number-format';

export const NumberSliderField = ({
  asCents = true,
  label,
  name,
  helpText,
  defaultValue,
  isDisabled,
  form,
  validators,
  prefillValue,
  prefillAction,
  disableScrollToElement,
  displayProperties,
  disableNextButton,
  increment,
  hideDivider,
  disableTitle,
  isTouched: defaultIsTouched,
  disableActiveElement,
  submitOnChange,
  hideBorder,
  hiddenComponent,
}: InputFieldParams<number> & {
  asCents?: boolean;
  increment?: number;
}) => {
  const { siteConfig } = useSiteConfigContext();
  const [isTouched, setTouched] = useState<boolean>(!!prefillValue || defaultIsTouched);
  const [fieldError, setFieldError] = useState<string | undefined>(
    getFieldError({ errors: form?.errors, isTouched, name }),
  );
  const [isRequired] = useState<boolean>(
    !!validators?.find(({ validation }) => validation.type === ValidationTypes.REQUIRED),
  );
  const defaultFormControl = useForm().control;
  const { activeElement, setActiveElement } = displayProperties;
  const numberSliderRef = useRef<any>();
  const isActive = activeElement.elementId === name || activeElement.elementId === `stepped-form-next-button-${name}`;
  const { disabledFromPrefill, hiddenFromPrefill } = prefillBehavior({
    prefillAction,
    prefillValue,
    options: undefined,
    validate: (value) =>
      getValidationMessage({
        validators: validators,
        value,
        props: undefined,
      }),
  });

  useEffect(() => {
    if (isActive && hiddenFromPrefill.display && setActiveElement && !isTouched) {
      setActiveElement({ elementId: getNextComponentId({ ...displayProperties, key: name }) });
    }
  }, [hiddenFromPrefill.display, isActive]);

  const errors = form?.errors;
  useEffect(() => {
    setFormFocusOrBlur({
      activeElement,
      scrollToId: name,
      disableScrollToElement,
      isFirstElement: displayProperties.index === 0,
      nextButtonFocus: true,
    });
  }, [JSON.stringify(errors), isTouched, name, isActive]);

  const minValidator = validators?.find(({ validation }) => validation.min);
  const maxValidator = validators?.find(({ validation }) => validation.max);

  const min = minValidator?.validation.min ? minValidator?.validation.min : undefined;
  const max = maxValidator?.validation.max ? maxValidator?.validation.max : undefined;

  return (
    <div id={name ? `${name}-form-group` : undefined} style={hiddenFromPrefill}>
      <StyledSliderRootSchemaComponent
        className='schema-component'
        isActive={isActive}
        disableActiveElement={disableActiveElement}
        isFirstElement={displayProperties.index === 0}
        hiddenComponent={hiddenComponent}
      >
        <Field
          isTouched={isTouched}
          disableTitle={disableTitle}
          isActive={isActive}
          name={name}
          hiddenComponent={hiddenComponent}
          onClick={() => setActiveElement({ elementId: name })}
          label={label}
          errors={fieldError ? [fieldError] : []}
          isRequired={isRequired}
          helpText={helpText}
          style={{
            color: getSteppedFormLabelColor({ isActive, siteConfig, color: Colors.Body }),
            fontSize: 16,
          }}
        >
          <Controller
            name={name}
            control={form?.control || defaultFormControl}
            defaultValue={defaultValue || min}
            rules={{
              required: isRequired ? 'Required' : undefined,
              validate: (value) =>
                getValidationMessage({
                  validators: validators,
                  value,
                  props: undefined,
                }),
            }}
            render={({ onChange, value }) => {
              return (
                <NumberSliderWrapper>
                  <NumberSliderValueDisplay
                    isActive={isActive}
                    color={getSteppedFormLabelColor({
                      isActive: isActive || isTouched,
                      siteConfig,
                      color: Colors.Body,
                    })}
                  >
                    {formatToNumber({ number: value })}
                  </NumberSliderValueDisplay>
                  <StyledSlider
                    defaultValue={min}
                    sliderRailColor={
                      isActive || isTouched
                        ? getColor({ siteConfig, color: 'border' })
                        : getSteppedFormLabelColor({ isActive, siteConfig, color: Colors.Body })
                    }
                    disabled={isDisabled || disabledFromPrefill}
                    max={max}
                    ref={numberSliderRef}
                    min={min}
                    step={increment || 1}
                    primaryColor={
                      isActive || isTouched
                        ? getColor({ siteConfig, color: 'primary' })
                        : getSteppedFormLabelColor({ isActive, siteConfig, color: Colors.Body })
                    }
                    onChange={(incomingValue: any) => {
                      setActiveElement({ elementId: name });
                      setTouched(true);
                      setFieldError(
                        getValidationMessage({
                          validators: validators,
                          value: incomingValue,
                          props: undefined,
                        }),
                      );
                      onChange(incomingValue);
                      debounce('blur-number-on-change', () => onChange(incomingValue), 10);
                      debounce(
                        'blur-number-slider',
                        () => document.getElementById(`stepped-form-next-button-${name}`)?.focus(),
                        100,
                      );
                    }}
                    value={value === '' ? value : value}
                    style={{ ...getSteppedFormInputStyle({ isActive, siteConfig }), border: 'none' }}
                  />
                  <NumberFooter
                    color={
                      isActive
                        ? Colors.Subscript
                        : getSteppedFormLabelColor({ isActive, siteConfig, color: Colors.Body })
                    }
                  >
                    <NumberMinMaxLabel siteConfig={siteConfig}>
                      Min {min && formatToNumber({ number: min })}
                    </NumberMinMaxLabel>
                    <NumberMinMaxLabel siteConfig={siteConfig}>
                      Max {max && formatToNumber({ number: max })}
                    </NumberMinMaxLabel>
                  </NumberFooter>
                </NumberSliderWrapper>
              );
            }}
          />
        </Field>
        {!hiddenComponent && (
          <SteppedFormButton
            disableActiveElement={!!disableActiveElement}
            hideBorder={hideBorder}
            submitOnChange={submitOnChange}
            setTouched={(isTouched) => setTouched(isTouched)}
            hideDivider={hideDivider}
            nextComponentId={getNextComponentId({ ...displayProperties, key: name })}
            getValue={() => form?.watch(name)}
            parentName={name}
            isDisabled={!!fieldError}
            isActive={isActive}
            setFieldError={(fieldError) => setFieldError(fieldError)}
            setActiveElement={(elementId) =>
              setActiveElement({
                elementId,
              })
            }
            validationFunction={getValidationMessage}
            validators={validators}
            validationProps={{
              prefix: undefined,
            }}
          />
        )}
      </StyledSliderRootSchemaComponent>
    </div>
  );
};

const NumberSliderWrapper = styled.div`
  padding-top: 22px;
  padding-bottom: 22px;
  border: none;
  border-radius: 8px;
  justify-content: left;
  position: relative;
`;

const NumberMinMaxLabel = styled.div<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  color: ${({ siteConfig }) => getColor({ color: 'disabled', siteConfig })};
`;

const StyledSlider = styled(Slider)<{ primaryColor: string; sliderRailColor: string }>`
  .rc-slider {
    position: relative;
    width: 100%;
    height: 30px;
    padding: 5px 0;
    border-radius: 6px;
    touch-action: none;
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    margin-left: 14px;
    margin-right: 14px;
  }
  .rc-slider * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-rail {
    position: absolute;
    width: 100%;
    height: 4px;
    margin-top: 5px;
    background-color: ${({ sliderRailColor }) => sliderRailColor};
    border-radius: 6px;
  }
  .rc-slider-track {
    position: absolute;
    margin-top: 5px;
    height: 4px;
    background-color: ${({ primaryColor }) => primaryColor};
    border-radius: 6px;
  }
  .rc-slider-handle {
    position: absolute;
    width: 24px;
    height: 24px;
    margin-top: -5px;
    background-color: #fff;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    cursor: -webkit-grab;
    cursor: grab;
    opacity: 1;
    touch-action: pan-x;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  }
  .rc-slider-handle-dragging.rc-slider-handle-dragging.rc-slider-handle-dragging {
    box-shadow: 0 0 0 5px rgba(70, 74, 82, 0.2);
  }
  .rc-slider-handle:focus {
    outline: none;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  }
  .rc-slider-handle:focus-visible {
    border: none;
  }
  .rc-slider-handle-click-focused:focus {
    // border-color: #240e8b;
    box-shadow: unset;
  }
  .rc-slider-handle:hover {
  }
  .rc-slider-handle:active {
    cursor: -webkit-grabbing;
    cursor: grabbing;
  }
  .rc-slider-mark {
    position: absolute;
    top: 18px;
    left: 0;
    width: 100%;
    font-size: 12px;
  }
  .rc-slider-mark-text {
    position: absolute;
    display: inline-block;
    color: #999;
    text-align: center;
    vertical-align: middle;
    cursor: pointer;
  }
  .rc-slider-mark-text-active {
    color: #666;
  }
  .rc-slider-step {
    position: absolute;
    width: 100%;
    height: 4px;
    background: transparent;
    pointer-events: none;
  }
  .rc-slider-dot {
    position: absolute;
    bottom: -2px;
    width: 8px;
    height: 8px;
    vertical-align: middle;
    background-color: #fff;
    border-radius: 50%;
    cursor: pointer;
  }
  .rc-slider-dot-active {
    border-color: none;
    border: none;
  }
  .rc-slider-dot-reverse {
    margin-right: -4px;
  }
  .rc-slider-disabled {
    background-color: #e9e9e9;
  }
  .rc-slider-disabled .rc-slider-track {
    background-color: #ccc;
  }
  .rc-slider-disabled .rc-slider-handle,
  .rc-slider-disabled .rc-slider-dot {
    background-color: #fff;
    border-color: #ccc;
    box-shadow: none;
    cursor: not-allowed;
  }
  .rc-slider-disabled .rc-slider-mark-text,
  .rc-slider-disabled .rc-slider-dot {
    cursor: not-allowed !important;
  }

  .rc-slider-tooltip-zoom-down-enter,
  .rc-slider-tooltip-zoom-down-appear {
    display: block !important;
    animation-duration: 0.3s;
    animation-fill-mode: both;
    animation-play-state: paused;
  }
  .rc-slider-tooltip-zoom-down-leave {
    display: block !important;
    animation-duration: 0.3s;
    animation-fill-mode: both;
    animation-play-state: paused;
  }
  .rc-slider-tooltip-zoom-down-enter.rc-slider-tooltip-zoom-down-enter-active,
  .rc-slider-tooltip-zoom-down-appear.rc-slider-tooltip-zoom-down-appear-active {
    animation-name: rcSliderTooltipZoomDownIn;
    animation-play-state: running;
  }
  .rc-slider-tooltip-zoom-down-leave.rc-slider-tooltip-zoom-down-leave-active {
    animation-name: rcSliderTooltipZoomDownOut;
    animation-play-state: running;
  }
  .rc-slider-tooltip-zoom-down-enter,
  .rc-slider-tooltip-zoom-down-appear {
    transform: scale(0, 0);
    animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
  }
  .rc-slider-tooltip-zoom-down-leave {
    animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
  }
  @keyframes rcSliderTooltipZoomDownIn {
    0% {
      transform: scale(0, 0);
      transform-origin: 50% 100%;
      opacity: 0;
    }
    100% {
      transform: scale(1, 1);
      transform-origin: 50% 100%;
    }
  }
  @keyframes rcSliderTooltipZoomDownOut {
    0% {
      transform: scale(1, 1);
      transform-origin: 50% 100%;
    }
    100% {
      transform: scale(0, 0);
      transform-origin: 50% 100%;
      opacity: 0;
    }
  }
  .rc-slider-tooltip {
    position: absolute;
    top: -9999px;
    left: -9999px;
    visibility: visible;
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-tooltip * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .rc-slider-tooltip-hidden {
    display: none;
  }
  .rc-slider-tooltip-placement-top {
    padding: 4px 0 8px 0;
  }
  .rc-slider-tooltip-inner {
    min-width: 24px;
    height: 24px;
    padding: 6px 2px;
    color: #fff;
    font-size: 12px;
    line-height: 1;
    text-align: center;
    text-decoration: none;
    background-color: #6c6c6c;
    border-radius: 6px;
    box-shadow: 0 0 4px #d9d9d9;
  }
  .rc-slider-tooltip-arrow {
    position: absolute;
    width: 0;
    height: 0;
    border-color: transparent;
  }
  .rc-slider-tooltip-placement-top .rc-slider-tooltip-arrow {
    bottom: 4px;
    left: 50%;
    margin-left: -4px;
    border-width: 4px 4px 0;
    border-top-color: #6c6c6c;
  }
`;

const NumberSliderValueDisplay = styled.span<{
  isActive: boolean;
  color: string;
}>`
  font-weight: 400;
  font-size: 25px;
  line-height: 30px;
  text-align: center;
  justify-content: center;
  display: flex;
  margin-bottom: 17px;
  color: ${({ color }) => color};
`;

const NumberFooter = styled.div<{ color: string }>`
  grid-template-columns: 1fr 1fr;
  width: 100%;
  display: grid;
  padding-top: 30px;

  div {
    font-size: 12px;
    color: ${Colors.Subscript};
  }

  div + div {
    width: 100%;
    display: flex;
    justify-content: end;
  }
`;
